import React, { useCallback, useLayoutEffect, useState } from 'react';
import { Icon, STATUS_MAP, StatusChip } from '../components/sp-primitives';
import { DeletePaymentConfirmDialog, Modal } from '../components/sp-overlays';
import { PShell, SpWorkspaceLoading, useApp } from '../components/sp-shell';
import { ROUTES } from '../routes';

function firstName(value) {
  const name = String(value || '').trim();
  if (!name || name === '—') return '—';
  return name.split(/\s+/)[0] || '—';
}

function PPayments() {
  const app = useApp();
  const [filter, setFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [archivedModal, setArchivedModal] = useState(false);
  const [archivedBatches, setArchivedBatches] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [approvalBlockedModal, setApprovalBlockedModal] = useState(false);
  const activeCompanyId = Number(app.activeCompany?.id || 0);
  const permissions = Array.isArray(app.currentUser?.permissions) ? app.currentUser.permissions : [];
  const roleText = String(app.currentUser?.role || '').toLowerCase();
  const isAdmin = permissions.includes('Administrador');
  const isRegistrar = permissions.includes('Registrador') || roleText.includes('registrador');

  const openDeleteDialog = useCallback((b) => {
    const isOwner = Number(b?.createdById || 0) > 0 && Number(b.createdById) === Number(app.currentUser?.id || 0);
    if (isRegistrar && !isAdmin && isOwner && b?.status === 'approval') {
      setApprovalBlockedModal(true);
      return;
    }
    const canMutatePayment = isAdmin || !isRegistrar || isOwner;
    if (!canMutatePayment) {
      app.toast('error', 'Sem permissão', 'Registradores só podem excluir pagamentos criados por eles.');
      return;
    }
    if (b?.status === 'sent' || b?.status === 'transmitted') {
      app.toast('info', 'Excluir lote', 'Pagamento em fila de transmissão ou já transmitido ao Sienge, não pode ser excluído.');
      return;
    }
    setDeleteTarget(b);
  }, [app, isAdmin, isRegistrar]);

  const closeDeleteDialog = useCallback(() => {
    if (deleteBusy) return;
    setDeleteTarget(null);
  }, [deleteBusy]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      const ok = await app.deleteBatch(deleteTarget.id);
      if (ok) setDeleteTarget(null);
    } finally {
      setDeleteBusy(false);
    }
  }, [app, deleteTarget]);

  useLayoutEffect(() => {
    if (!app.workspaceReady) return;
    if (app.companies.length === 0 && app.templates.length === 0) {
      app.navigate(ROUTES.EMPTY, { shell: 'payments' });
    }
  }, [app.workspaceReady, app.companies.length, app.templates.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!app.workspaceReady) {
    return <SpWorkspaceLoading active="payments" title="A carregar pagamentos…" />;
  }
  if (app.companies.length === 0 && app.templates.length === 0) {
    return null;
  }
  const companyBatches = app.batches.filter((b) => Number(b.companyId || 0) === activeCompanyId);
  const filtered = filter === 'all' ? companyBatches : companyBatches.filter((b) => b.status === filter);
  return (
    <PShell active="payments">
      <div className="sp-screen-head">
        <div>
          <div className="sp-caps sp-screen-head__caps">Pagamentos enviados</div>
          <h1 className="sp-screen-head__title">Solicitações de Pagamento</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="sp-btn sp-btn-outline" onClick={async () => {
            setArchivedModal(true);
            setArchivedLoading(true);
            try {
              const batches = await app.fetchArchivedBatches(activeCompanyId);
              setArchivedBatches(batches || []);
            } catch { setArchivedBatches([]); }
            finally { setArchivedLoading(false); }
          }}>Ver Arquivados</button>
          <button type="button" className="sp-btn sp-btn-accent" onClick={() => app.navigate(ROUTES.PAYMENT_NEW)}><Icon name="plus" size={13} /> Novo pagamento</button>
        </div>
      </div>

      <div className="sp-filter-row">
        {[['all', 'Todos'], ['pending', 'Pendentes'], ['transmitted', 'Transmitidos'], ['sent', 'Aprovados'], ['approval', 'Aprovação'], ['processing', 'Processando'], ['rejected', 'Rejeitados'], ['error', 'Erros']].map(([k, l]) => {
          const n = k === 'all' ? companyBatches.length : companyBatches.filter((b) => b.status === k).length;
          const on = filter === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              className={`sp-filter-chip${on ? ' is-active' : ''}`}
            >
              {l} · <span className="sp-num">{n}</span>
            </button>
          );
        })}
      </div>

      <div className="sp-card sp-card-flush">
        <table className="sp-table">
          <thead><tr><th>Lote</th><th>Credor</th><th>Criado Em</th><th>Criado Por</th><th>Vencimento</th><th>Doc. Sienge</th><th>Modelo / Arquivo</th><th className="sp-th-num">Valor</th><th className="sp-th-num sp-payments-th-credores">CREDOR(ES)</th><th>Status</th><th /></tr></thead>
          <tbody>
            {filtered.map((b) => {
              const s = STATUS_MAP[b.status] || STATUS_MAP.draft;
              const isOwner = Number(b?.createdById || 0) > 0 && Number(b.createdById) === Number(app.currentUser?.id || 0);
              const approvalBlocked = isRegistrar && !isAdmin && isOwner && b.status === 'approval';
              const showDelete = b.status !== 'transmitted' && b.status !== 'sent' && (isAdmin || !isRegistrar || isOwner);
              const docId = b.siengeDocumentId && String(b.siengeDocumentId).trim();
              const docCell = docId
                ? <span className="sp-chip sp-mono">{docId}</span>
                : b.status === 'pending'
                  ? <span className="sp-chip sp-chip-red">Pendente</span>
                : b.status === 'approval'
                  ? <span className="sp-chip sp-chip-amber">Aguardando Aprovação</span>
                : b.status === 'rejected'
                  ? <span className="sp-chip sp-chip-purple">Rejeitado</span>
                  : <span className="sp-mono sp-td-date">—</span>;
              return (
                <tr key={b.id}>
                  <td className="sp-mono sp-fw-700">{b.id}</td>
                  <td>
                    {b.creditorNames.length === 0 && <span className="sp-mono sp-td-date">—</span>}
                    {b.creditorNames.length === 1 && <span>{b.creditorNames[0]}</span>}
                    {b.creditorNames.length > 1 && <span>{b.creditorNames[0]} <span className="sp-mono sp-td-date">+{b.creditorNames.length - 1}</span></span>}
                  </td>
                  <td className="sp-mono sp-td-date">{b.date}</td>
                  <td>{firstName(b.createdByName)}</td>
                  <td className="sp-mono sp-td-date">{b.dueDate ?? '—'}</td>
                  <td>{docCell}</td>
                  <td>
                    <div className="sp-cell-stack-title">{b.template}</div>
                    <div className="sp-mono sp-cell-stack-file">{b.file}</div>
                  </td>
                  <td className="sp-num sp-td-num">{b.total}</td>
                  <td className="sp-num sp-td-muted-num">{b.colab}</td>
                  <td><StatusChip kind={s.kind}>{s.label}</StatusChip></td>
                  <td className="sp-td-actions">
                    <div className="sp-payments-row-actions">
                      <button type="button" className="sp-btn sp-btn-ghost sp-btn-row-action" onClick={() => app.navigate('batch-details', { id: b.id })}>Detalhes</button>
                      {showDelete && (
                        <button
                          type="button"
                          className="sp-btn sp-btn-ghost sp-btn-row-action sp-btn-row-action--danger"
                          disabled={approvalBlocked}
                          onClick={() => openDeleteDialog(b)}
                        >
                          Excluir
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <DeletePaymentConfirmDialog
        open={Boolean(deleteTarget)}
        batchId={deleteTarget?.id}
        onCancel={closeDeleteDialog}
        onConfirm={confirmDelete}
        busy={deleteBusy}
      />

      <Modal open={archivedModal} onClose={() => setArchivedModal(false)} width={900}>
        <div className="sp-modal__head">
          <div>
            <div className="sp-modal__head-title">Pagamentos Arquivados</div>
            <div className="sp-modal__head-desc">Pagamentos rejeitados há mais de 3 dias.</div>
          </div>
          <button type="button" className="sp-btn sp-btn-ghost sp-modal__close" onClick={() => setArchivedModal(false)}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="sp-modal__body">
          {archivedLoading ? (
            <div className="sp-creditors-banner">Carregando arquivados…</div>
          ) : archivedBatches.length === 0 ? (
            <div className="sp-creditors-banner">Nenhum pagamento arquivado.</div>
          ) : (
            <table className="sp-table">
              <thead>
                <tr><th>Lote</th><th>Credor</th><th>Criado Em</th><th>Vencimento</th><th>Modelo</th><th className="sp-th-num">Valor</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {archivedBatches.map((b) => {
                  const s = STATUS_MAP[b.status] || STATUS_MAP.draft;
                  return (
                    <tr key={b.id}>
                      <td className="sp-mono sp-fw-700">{b.id}</td>
                      <td>
                        {b.creditorNames?.length > 0 ? b.creditorNames[0] : <span className="sp-mono sp-td-date">—</span>}
                        {b.creditorNames?.length > 1 && <span className="sp-mono sp-td-date"> +{b.creditorNames.length - 1}</span>}
                      </td>
                      <td className="sp-mono sp-td-date">{b.date}</td>
                      <td className="sp-mono sp-td-date">{b.dueDate ?? '—'}</td>
                      <td>{b.template}</td>
                      <td className="sp-num sp-td-num">{b.total}</td>
                      <td><StatusChip kind={s.kind}>{s.label}</StatusChip></td>
                      <td className="sp-td-actions">
                        <button type="button" className="sp-btn sp-btn-ghost sp-btn-row-action" onClick={() => { setArchivedModal(false); app.navigate('batch-details', { id: b.id }); }}>Detalhes</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Modal>
      <Modal open={approvalBlockedModal} onClose={() => setApprovalBlockedModal(false)} width={460}>
        <div className="sp-modal__head">
          <div>
            <div className="sp-modal__head-title">Pagamento bloqueado para aprovação</div>
            <div className="sp-modal__head-desc">Ele só poderá ser editado se for rejeitado pelos aprovadores.</div>
          </div>
          <button type="button" className="sp-btn sp-btn-ghost sp-modal__close" onClick={() => setApprovalBlockedModal(false)}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="sp-modal__body sp-modal__body--stack-lg">
          <div className="sp-modal-actions">
            <button type="button" className="sp-btn sp-btn-outline" onClick={() => setApprovalBlockedModal(false)}>
              Entendi
            </button>
          </div>
        </div>
      </Modal>
    </PShell>
  );
}

export { PPayments };
